import { useCallback, useEffect, useRef } from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

/**
 * Validation timing of the account forms (like the contact form on it-academieoverheid.nl). Nothing is validated before
 * the first submit (`mode` and `reValidateMode: 'onSubmit'`), so no message appears silently on blur (WCAG 4.1.3). The
 * returned `validate` is what the submit button calls: it validates the given fields at once and focuses the first
 * invalid one. From then on a field is re-validated whenever its value changes, so its message follows the input
 * (empty, then invalid, then gone). That includes changes made by code (`setValue`): the nickname filled in from the
 * name must lose its "required" message too. React Hook Form's own `reValidateMode: 'onChange'` does this only after
 * `handleSubmit`, which these forms do not use for their buttons.
 */
export const useSubmitValidation = <TFieldValues extends FieldValues>({ watch, trigger }: Pick<UseFormReturn<TFieldValues>, 'watch' | 'trigger'>) => {
    const submitted = useRef(false);

    useEffect(() => {
        const subscription = watch((_, { name }) => {
            if (submitted.current && name) trigger(name);
        });
        return () => subscription.unsubscribe();
    }, [watch, trigger]);

    return useCallback(
        (fields: FieldPath<TFieldValues>[]) => {
            submitted.current = true;
            // In the order of the form: trigger focuses the first invalid field in the order of this list
            return trigger(fields, { shouldFocus: true });
        },
        [trigger]
    );
};
